import { createFileRoute, Link, Outlet, useLocation, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminNavigation } from "@/components/admin/AdminNavigation";
import { supabase } from "@/integrations/supabase/client";
import { adminAuthQuery } from "@/lib/query-registry";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [{ title: "Admin · Shugert" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: AdminLayout,
});

function AdminLayout() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const location = useLocation();
  const isLoginRoute = location.pathname.startsWith("/admin/login");
  const [email, setEmail] = useState<string | null>(null);

  const authQuery = useQuery({ ...adminAuthQuery(), enabled: !isLoginRoute });

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setEmail(data.session?.user.email ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setEmail(session?.user.email ?? null);
      queryClient.invalidateQueries({ queryKey: ["admin.auth.check"] });
    });
    return () => sub.subscription.unsubscribe();
  }, [queryClient]);

  useEffect(() => {
    if (!authQuery.isError) return;
    const status = (authQuery.error as Error & { status?: number } | null)?.status;
    if (status === 401) {
      router.navigate({ to: "/admin/login" });
    }
  }, [authQuery.isError, authQuery.error, router]);

  const signOut = async () => {
    await supabase.auth.signOut();
    router.navigate({ to: "/admin/login" });
  };

  return (
    <div className="min-h-dvh bg-background">
      {!isLoginRoute && (
        <header className="border-b border-border bg-card/30">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4">
            <Link
              to="/admin/north-star"
              className="shrink-0 font-display text-lg font-semibold text-foreground"
            >
              Shugert Admin
            </Link>
            <div className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground">
              <Link to="/" className="hover:text-foreground">
                View site →
              </Link>
              {email && (
                <>
                  <span className="hidden xl:inline">{email}</span>
                  <button
                    onClick={signOut}
                    className="rounded-full border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-card"
                  >
                    Sign out
                  </button>
                </>
              )}
            </div>
          </div>
          <AdminNavigation />
        </header>
      )}
      <main className={isLoginRoute ? "" : "mx-auto max-w-7xl px-6 py-10"}>
        {isLoginRoute ? <Outlet /> : <AuthGate authQuery={authQuery} onSignOut={signOut} />}
      </main>
    </div>
  );
}

type AuthReason = {
  code: "no_admin_role" | "rls_blocked" | "lookup_failed";
  message: string;
  details?: string | null;
} | null;

type AuthQuery = ReturnType<
  typeof useQuery<{
    isAdmin: boolean;
    userId: string;
    email?: string | null;
    reason?: AuthReason;
  }>
>;

function AuthGate({ authQuery, onSignOut }: { authQuery: AuthQuery; onSignOut: () => void }) {
  if (authQuery.isPending) {
    return (
      <div className="space-y-3">
        <div className="h-8 w-48 animate-pulse rounded bg-card" />
        <div className="h-4 w-72 animate-pulse rounded bg-card" />
        <div className="mt-6 h-32 animate-pulse rounded-2xl bg-card" />
      </div>
    );
  }

  if (authQuery.isError) {
    const status = (authQuery.error as Error & { status?: number } | null)?.status;
    if (status === 401) {
      return (
        <div className="py-24 text-center text-sm text-muted-foreground">
          <p>Redirecting to sign-in…</p>
          <Link
            to="/admin/login"
            className="mt-4 inline-block rounded-full border border-border px-5 py-2 text-sm font-medium text-foreground hover:bg-card"
          >
            Go to sign-in
          </Link>
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-lg py-12 text-center">
        <h1 className="font-display text-xl font-semibold text-foreground">
          Couldn't verify admin access
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {authQuery.error?.message || "Something went wrong."}
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            onClick={() => authQuery.refetch()}
            className="rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background hover:opacity-90"
          >
            Retry
          </button>
          <button
            onClick={onSignOut}
            className="rounded-full border border-border px-5 py-2 text-sm font-medium text-foreground hover:bg-card"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const data = authQuery.data;
  if (!data || data.isAdmin !== true) {
    return (
      <div className="mx-auto max-w-lg py-12 text-center">
        <h1 className="font-display text-xl font-semibold text-foreground">
          Admin access required
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {data?.reason?.message ||
            "Your session isn't signed in as an admin, so admin data can't be loaded."}
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Link
            to="/admin/login"
            className="rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background hover:opacity-90"
          >
            Go to sign-in
          </Link>
          <button
            onClick={() => authQuery.refetch()}
            className="rounded-full border border-border px-5 py-2 text-sm font-medium text-foreground hover:bg-card"
          >
            Retry
          </button>
          <button
            onClick={onSignOut}
            className="rounded-full border border-border px-5 py-2 text-sm font-medium text-foreground hover:bg-card"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
